import { apiFetch } from "./api";

// Client half of Web Push (see webpush.js for the server half). Entirely
// optional and additive - every function here fails soft (returns
// false/null) rather than throwing, since push is a nice-to-have on top of
// the in-app notification bell/WS pushes that already work without it.

export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

// applicationServerKey needs raw bytes, not the base64url string the
// server hands out - standard conversion, no library needed for it.
function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // Explicit ArrayBuffer (not the default Uint8Array<ArrayBufferLike>) -
  // PushSubscriptionOptionsInit.applicationServerKey's DOM typing wants a
  // real BufferSource, which SharedArrayBuffer-inclusive ArrayBufferLike
  // doesn't satisfy.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export async function getPushSubscriptionStatus(): Promise<{ supported: boolean; configured: boolean; subscribed: boolean }> {
  if (!isPushSupported()) return { supported: false, configured: false, subscribed: false };
  try {
    const res = await apiFetch("/api/push/vapid-public-key");
    if (!res.ok) return { supported: true, configured: false, subscribed: false };
    const data = await res.json();
    if (!data.configured) return { supported: true, configured: false, subscribed: false };

    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    return { supported: true, configured: true, subscribed: !!existing };
  } catch {
    return { supported: isPushSupported(), configured: false, subscribed: false };
  }
}

export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return false;

    const keyRes = await apiFetch("/api/push/vapid-public-key");
    if (!keyRes.ok) return false;
    const { publicKey, configured } = await keyRes.json();
    if (!configured || !publicKey) return false;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    const res = await apiFetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return true;

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await apiFetch("/api/push/unsubscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint }),
    });
    return true;
  } catch {
    return false;
  }
}
